const MAX_PREVIEW_BYTES = 8 * 1024 * 1024;

const PREVIEW_ATTEMPTS = [
  { edge: 2200, type: 'image/webp', quality: 0.82 },
  { edge: 1800, type: 'image/webp', quality: 0.76 },
  { edge: 1600, type: 'image/jpeg', quality: 0.78 },
  { edge: 1400, type: 'image/jpeg', quality: 0.72 }
] as const;

export interface WebPreview {
  blob: Blob;
  contentType: 'image/jpeg' | 'image/webp';
  sizeBytes: number;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
}

export async function createWebPreview(file: File, signal?: AbortSignal): Promise<WebPreview> {
  throwIfAborted(signal);
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    throwIfAborted(signal);
    for (const attempt of PREVIEW_ATTEMPTS) {
      throwIfAborted(signal);
      const { width, height } = previewSize(bitmap.width, bitmap.height, attempt.edge);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: attempt.type === 'image/webp' });
      if (!context) {
        throw new Error('Could not prepare image preview');
      }
      context.drawImage(bitmap, 0, 0, width, height);
      const blob = await canvasToBlob(canvas, attempt.type, attempt.quality);
      throwIfAborted(signal);
      if (blob.size <= MAX_PREVIEW_BYTES) {
        return {
          blob,
          contentType: blob.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
          sizeBytes: blob.size,
          width,
          height,
          originalWidth: bitmap.width,
          originalHeight: bitmap.height
        };
      }
    }
  } finally {
    bitmap.close();
  }

  throw new Error('Preview could not be compressed below 8MB');
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Photo upload was cancelled.', 'AbortError');
  }
}

function previewSize(width: number, height: number, maxLongEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  const scale = Math.min(1, maxLongEdge / longEdge);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/webp', quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Could not encode ${type} preview`));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
}
